"use client";

import Link from "next/link";
import posthog from "posthog-js";
import { Logo } from "@/components/logo";
import { BRAND } from "@/lib/brand";

interface MarketingHeaderProps {
  variant?: "light" | "dark";
}

const trackHeaderCta = (cta: string) => {
  if (typeof window !== "undefined" && posthog?.__loaded) {
    posthog.capture("marketing_cta_clicked", { cta, location: "header" });
  }
};

export function MarketingHeader({ variant = "light" }: MarketingHeaderProps) {
  const isDark = variant === "dark";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 border-b px-4 sm:px-6 ${
        isDark
          ? "bg-gray-900/80 backdrop-blur-md border-gray-800"
          : "bg-white/80 backdrop-blur-md border-gray-200"
      }`}
    >
      <div className="max-w-7xl mx-auto py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Left side: Logo + Navigation */}
          <div className="flex items-center gap-6 sm:gap-8">
            <Link href="/" className="flex items-center gap-2">
              <Logo
                size="md"
                className={isDark ? "text-white" : "text-gray-900"}
              />
              <span
                className={`text-base sm:text-lg font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {BRAND.name}
              </span>
            </Link>
            <nav className="hidden sm:flex items-center gap-6">
              <Link
                href="/#features"
                className={`text-sm font-medium transition-colors ${
                  isDark
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => trackHeaderCta("header_features")}
              >
                Features
              </Link>
              <Link
                href="/pricing"
                className={`text-sm font-medium transition-colors ${
                  isDark
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => trackHeaderCta("header_pricing")}
              >
                Pricing
              </Link>
            </nav>
          </div>

          {/* Right side: Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              href="/auth"
              className={`text-sm sm:text-base font-medium transition-colors ${
                isDark
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => trackHeaderCta("header_sign_in")}
            >
              Sign in
            </Link>
            <Link
              href="/auth"
              className={`px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base font-semibold transition-colors rounded-lg ${
                isDark
                  ? "bg-white text-gray-900 hover:bg-gray-100"
                  : "bg-gray-900 text-white hover:bg-gray-800"
              }`}
              onClick={() => trackHeaderCta("header_get_started")}
            >
              <span className="hidden sm:inline">Get started</span>
              <span className="sm:hidden">Sign up</span>
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
